import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Check, X } from 'lucide-react';
import { getMyAds, publishAd } from '../../../services/p2pService';
import { useToast } from '../../../context/ToastContext';
import { authFetch, getAuthHeaders } from '../../../lib/authFetch';

export default function PostAdPreview() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [ad, setAd] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [adPostFee, setAdPostFee] = useState(0.02);
  const [adPostFeeType, setAdPostFeeType] = useState<'percentage' | 'fixed'>('percentage');
  const [adPostFeeEnabled, setAdPostFeeEnabled] = useState(true);
  const [published, setPublished] = useState(false);

  useEffect(()=>{
    const id = sessionStorage.getItem('p2p_draft_id');
    if (!id) { navigate('/p2p/post-ad/1'); return; }
    // Fetch draft directly by ID from sessionStorage step data
    const step1 = JSON.parse(sessionStorage.getItem('p2p_step1') || '{}')
    const step2 = JSON.parse(sessionStorage.getItem('p2p_step2') || '{}')
    const step3 = JSON.parse(sessionStorage.getItem('p2p_step3') || '{}')
    // Try to load from API first, fall back to session data
    import('../../../lib/authFetch').then(({ authFetch, getAuthHeaders }) => {
      authFetch(`${import.meta.env.VITE_API_BASE_URL}/api/p2p/ads/${id}`, { headers: getAuthHeaders() })
        .then(r => r.json())
        .then(d => {
          if (d?.data) { setAd(d.data); return; }
          // Fallback: construct preview from session storage
          if (step1.crypto) setAd({ _id: id, ...step1, ...step2, ...step3, status: 'draft' });
          else navigate('/p2p/post-ad/1');
        })
        .catch(() => {
          if (step1.crypto) setAd({ _id: id, ...step1, ...step2, ...step3, status: 'draft' });
          else navigate('/p2p/post-ad/1');
        })
    });
    // Fetch real fee from backend
    authFetch(`${import.meta.env.VITE_API_BASE_URL}/api/p2p/settings/fees`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d?.data?.fees) {
          setAdPostFee(d.data.fees.adPostFee ?? 0.02);
          setAdPostFeeType(d.data.fees.adPostFeeType ?? 'percentage');
          setAdPostFeeEnabled(d.data.fees.adPostFeeEnabled ?? true);
        }
      }).catch(() => {});
  },[]);

  const publish = async () => {
    const id = sessionStorage.getItem('p2p_draft_id');
    if (!id||!ad) return;
    setLoading(true);
    try {
      await publishAd(id);
      sessionStorage.removeItem('p2p_draft_id');
      sessionStorage.removeItem('p2p_step1');
      sessionStorage.removeItem('p2p_step2');
      sessionStorage.removeItem('p2p_step3');
      setPublished(true);
    } catch(e:any){ toast(e.message,'error'); }
    setLoading(false);
  };

  if (!ad) return <div className="p2p-loading"><div className="p2p-spinner"/></div>;
  const feeAmount = adPostFeeType === 'fixed' ? adPostFee : (ad.totalAmount ?? 0) * adPostFee;
  const feeDisplay = parseFloat(feeAmount.toFixed(8)).toString();

  // ─── Ad Successfully Posted screen ───
  if (published) {
    const pms = ad.paymentMethods ?? [];
    return (
      <div style={{ background:'var(--p2p-bg-primary)', minHeight:'100dvh', display:'flex', flexDirection:'column', padding:'20px 16px 24px', position:'relative' }}>
        <button onClick={()=>navigate('/p2p/ads')} style={{ position:'absolute', top:18, right:18, background:'none', border:'none', cursor:'pointer', color:'var(--p2p-text-secondary)' }}>
          <X size={22}/>
        </button>

        <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center' }}>
          <div style={{ width:80, height:80, borderRadius:'50%', background:'rgba(28,212,167,0.15)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
            <div style={{ width:58, height:58, borderRadius:'50%', background:'#1CD4A7', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Check size={30} color="#000" strokeWidth={3}/>
            </div>
          </div>
          <div style={{ fontSize:22, fontWeight:700, color:'var(--p2p-text-primary)', marginBottom:8 }}>Ad Successfully Posted</div>
          <div style={{ fontSize:14, color:'var(--p2p-text-secondary)', lineHeight:1.6, marginBottom:24, maxWidth:320 }}>
            Your ad has been published and users can now place orders. Please pay attention to the prompt for new orders.
          </div>

          {/* Ad summary card */}
          <div style={{ width:'100%', maxWidth:400, textAlign:'left', background:'var(--p2p-bg-secondary)', border:'1px solid var(--p2p-border-default)', borderRadius:12, padding:16 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:15, fontWeight:700, color: ad.side==='sell' ? 'var(--p2p-sell)' : 'var(--p2p-buy)' }}>{ad.side==='sell'?'Sell':'Buy'}</span>
                <span style={{ fontSize:15, fontWeight:700, color:'var(--p2p-text-primary)' }}>{ad.crypto}</span>
                <span style={{ fontSize:14, color:'var(--p2p-text-secondary)' }}>With</span>
                <span style={{ fontSize:15, fontWeight:700, color:'var(--p2p-text-primary)' }}>{ad.fiat}</span>
              </div>
              <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:4, background:'rgba(28,212,167,0.15)', color:'#1CD4A7' }}>Online</span>
            </div>
            <div style={{ fontSize:24, fontWeight:700, color:'var(--p2p-text-primary)', marginBottom:12, fontVariantNumeric:'tabular-nums' }}>
              <span style={{ fontSize:13, fontWeight:500, color:'var(--p2p-text-muted)' }}>{ad.fiat==='KES'?'KSh ':''}</span>
              {(ad.currentPrice??ad.fixedPrice??0).toLocaleString()}
            </div>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:13,color:'var(--p2p-text-secondary)'}}>Total Amount</span><span style={{fontSize:13,color:'var(--p2p-text-primary)',fontWeight:500}}>{parseFloat((ad.totalAmount??0).toFixed(4)).toString()} {ad.crypto}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:13,color:'var(--p2p-text-secondary)'}}>Limit</span><span style={{fontSize:13,color:'var(--p2p-text-primary)',fontWeight:500}}>{ad.minOrderAmount?.toLocaleString()} – {ad.maxOrderAmount?.toLocaleString()} {ad.fiat}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:13,color:'var(--p2p-text-secondary)'}}>Ad Number</span><span style={{fontSize:12,color:'var(--p2p-text-muted)',fontFamily:'monospace',wordBreak:'break-all'}}>{ad.orderNumber ?? ad._id}</span></div>
            {pms.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginTop:10, paddingTop:10, borderTop:'1px solid var(--p2p-border-default)' }}>
                {pms.map((m:any, i:number)=>(
                  <span key={i} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'var(--p2p-text-secondary)' }}>
                    <span style={{ width:3, height:12, borderRadius:2, background:m.methodColor??'#2563eb', flexShrink:0 }}/>
                    {m.methodType ?? m}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <button style={{width:'100%',height:52,background:'#F0B90B',color:'#000',fontWeight:700,fontSize:16,border:'none',borderRadius:8,cursor:'pointer',marginTop:24}} onClick={()=>navigate('/p2p/ads')}>Done</button>
      </div>
    );
  }

  return (
    <div style={{ background:'var(--p2p-bg-primary)', minHeight:'100vh' }}>
      <div className="p2p-header">
        <button className="p2p-back-btn" onClick={()=>navigate(-1)}><ArrowLeft size={20}/>Preview</button>
      </div>
      <div style={{ padding:16, display:'flex', flexDirection:'column', gap:16 }}>
        <div className="p2p-card">
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            <span className={`p2p-badge p2p-badge-${ad.side==='buy'?'buy':'sell'}`}>{ad.side.toUpperCase()}</span>
            <span style={{ fontSize:16, fontWeight:700, color:'var(--p2p-text-primary)' }}>{ad.crypto}/{ad.fiat}</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:13,color:'var(--p2p-text-secondary)'}}>Price</span><span style={{fontSize:13,color:'var(--p2p-text-primary)',fontWeight:500}}>{(ad.currentPrice??ad.fixedPrice??0).toLocaleString()} {ad.fiat} {ad.priceType==='floating'&&'(Floating)'}</span></div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:13,color:'var(--p2p-text-secondary)'}}>Total Amount</span><span style={{fontSize:13,color:'var(--p2p-text-primary)',fontWeight:500}}>{ad.totalAmount?.toFixed(4)} {ad.crypto}</span></div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:13,color:'var(--p2p-text-secondary)'}}>Order Limits</span><span style={{fontSize:13,color:'var(--p2p-text-primary)',fontWeight:500}}>{ad.minOrderAmount?.toLocaleString()} – {ad.maxOrderAmount?.toLocaleString()} {ad.fiat}</span></div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:13,color:'var(--p2p-text-secondary)'}}>Time Limit</span><span style={{fontSize:13,color:'var(--p2p-text-primary)',fontWeight:500}}>{ad.paymentTimeLimit} min</span></div>
          {ad.terms && <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:13,color:'var(--p2p-text-secondary)'}}>Terms</span><span className="p2p-row-value" style={{ maxWidth:200, textAlign:'right', fontSize:12 }}>{ad.terms}</span></div>}
        </div>

        <div className="p2p-info-bar">
          <AlertCircle size={16} style={{ flexShrink:0 }}/>
          {adPostFeeEnabled && <span>Publishing this ad will deduct a <strong>{feeDisplay} {ad.crypto}</strong> ({adPostFeeType === 'percentage' ? `${(adPostFee * 100).toFixed(2)}%` : 'flat'}) posting fee from your balance.</span>}
          {!adPostFeeEnabled && <span>Publishing this ad is <strong>free</strong> — no posting fee will be charged.</span>}
        </div>

        <button className="p2p-btn-yellow" disabled={loading} onClick={publish}>{loading?'Publishing...':'Publish Ad'}</button>
        <button className="p2p-btn-secondary" onClick={()=>navigate(-1)}>Edit</button>
      </div>
    </div>
  );
}
